<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAmountButtons } from '@/i18n/helper'

const { locale, t } = useI18n()
const { proxy } = getCurrentInstance();

const me = computed(() => proxy.$store.state.user)
const usdtExchangeRate = computed(() =>
  proxy.$store.state.settings.usdt_exchange_rate
    ? proxy.$store.state.settings.usdt_exchange_rate
    : null
)

// 탭 관련 상태
const activeTab = ref('normal') // 'normal' 또는 'usdt'

// 금액 버튼 데이터 정의
const amountButtons = getAmountButtons(locale.value, usdtExchangeRate.value)
const exchangeRate = Number(usdtExchangeRate.value?.ex_rate_sell)
const amount = ref('0')
const usdtAmount = ref('0')
const walletAddress = computed(() => proxy.$store.state.user.bank_account)
const isUsdtInputFocused = ref(false)
//**del
// const password = ref('')

// 탭 변경 함수
const changeTab = (tab) => {
  activeTab.value = tab
  // 탭 변경시 입력값 리셋
  resetAmount()
}

const fetchMyInfo = async () => {
  await proxy.$store.dispatch('fetchMyInfo')
}

const selectAmount = (event) => {
  const selectedAmount = Number(event.target.dataset.amount)
  const currentAmount = Number(amount.value) || 0
  amount.value = String(currentAmount + selectedAmount)
}

const resetAmount = () => {
  amount.value = '0'
  usdtAmount.value = '0'
}

const request = async (e) => {
  e.preventDefault()

  if (amount.value <= 0) {
    proxy.$swal.error(t('common.amountMustBeGreaterThan0'))
    return
  }

  const submitBtn = document.querySelector('button[type="submit"]')
  submitBtn.disabled = true

  let coinOrBankName = me.value.bank_name;
  if (activeTab.value === 'usdt') {
    coinOrBankName = 'USDT' // USDT 출금의 경우
  }

  await proxy.$store.dispatch('requestBanking', {
    deposit: false,
    amount: amount.value,
    coinOrBankName
  })

  submitBtn.disabled = false
}

watch(amount, (newAmount) => {
  if (exchangeRate && !isNaN(Number(newAmount))) {
    const calculatedUsdtAmount = Number((Number(newAmount) / exchangeRate).toFixed(2))
    if (String(calculatedUsdtAmount) !== usdtAmount.value) {
      usdtAmount.value = String(calculatedUsdtAmount)
    }
  } else if (newAmount === '' || isNaN(Number(newAmount))) {
    usdtAmount.value = '0'
  }
})

watch(usdtAmount, (newUsdtAmount) => {
  if (isUsdtInputFocused.value && exchangeRate && !isNaN(Number(newUsdtAmount))) {
    const calculatedAmount = Number((Number(newUsdtAmount) * exchangeRate).toFixed(2))
    if (String(calculatedAmount) !== amount.value) {
      amount.value = String(calculatedAmount)
    }
  } else if (newUsdtAmount === '' || isNaN(Number(newUsdtAmount))) {
    // 포커스가 없을 때는 amount를 0으로 만들지 않도록 하거나, 정책에 따라 결정
    // 여기서는 입력값이 비거나 숫자가 아닐 때만 amount를 0으로 설정합니다.
    if (!isUsdtInputFocused.value && (newUsdtAmount === '' || isNaN(Number(newUsdtAmount)))) {
      amount.value = '0'
    }
  }
})

const handleMyWallet = () => {
  if (usdtExchangeRate.value) {
    window.open(usdtExchangeRate.value.my_wallet, '_blank')
  }
}

onMounted(() => {
  fetchMyInfo()
})
</script>
<template>
  <form method="post" @submit="request">
    <div class="modal-banner">
      <div class="banner-text">{{ t('withdrawal.notice') }}</div>
      <img src="@/assets/img/banner/banner-1.png" alt="" />
    </div>

    <!-- 탭 네비게이션 -->
    <div class="tab-navigation mb-4">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'normal' }"
        @click="changeTab('normal')"
      >
        {{ t('withdrawal.normalWithdrawal') || '일반 출금' }}
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === 'usdt' }"
        @click="changeTab('usdt')"
      >
        {{ t('withdrawal.usdtWithdrawal') || 'USDT 출금' }}
      </button>
    </div>

    <!-- 일반 출금 탭 -->
    <div v-show="activeTab === 'normal'" class="normal-tab">
      <div class="mb-2">
        <label for="">{{ t('withdrawal.currentBalance') }}</label>
        <div class="flex gap-3">
          <input type="text" class="form-control" :value="$store.state.user.chipStr" readonly />
          <button
            type="button"
            class="btn btn-default w-12 min-w-fit btn-refresh"
            @click="fetchMyInfo"
          >
            <i class="fa-solid fa-arrows-rotate"></i>
          </button>
        </div>
      </div>
      <div class="mb-3">
        <label for="">{{ t('withdrawal.amount') }} (*)</label>
        <input
          type="number"
          step="any"
          class="form-control numbermoney"
          placeholder="0"
          name="amount"
          v-model="amount"
        />
      </div>
      <div class="flex items-center justify-end gap-3 mt-4 mb-2 amount-btns">
        <button
          v-for="btn in amountButtons"
          :key="btn.id"
          type="button"
          class="btn btn-secondary"
          :id="btn.id"
          @click="selectAmount"
          :data-amount="btn.amount"
        >
          {{ btn.label }}
        </button>
        <button type="button" class="btn btn-default" @click="resetAmount">
          {{ t('common.reset') }}
        </button>
      </div>
      <div class="flex items-center gap-3 justify-center mt-4 mb-2">
        <button type="submit" class="btn btn-default px-4">
          {{ t('common.submit') }}
        </button>
      </div>
    </div>

    <!-- USDT 출금 탭 -->
    <div v-show="activeTab === 'usdt'" class="usdt-tab">
      <div class="mb-2">
        <label for="">{{ t('withdrawal.currentBalance') }}</label>
        <div class="flex gap-3">
          <input type="text" class="form-control" :value="$store.state.user.chipStr" readonly />
          <button
            type="button"
            class="btn btn-default w-12 min-w-fit btn-refresh"
            @click="fetchMyInfo"
          >
            <i class="fa-solid fa-arrows-rotate"></i>
          </button>
        </div>
      </div>
      <div class="mb-2">
        <label for="">{{ t('withdrawal.walletAddress') }}</label>
        <div class="flex gap-3">
          <input type="text" :value="walletAddress" class="form-control" readonly />
        </div>
      </div>
      
      <div class="flex flex-1 gap-3">
        <div class="mb-3" style="flex: auto;">
          <label for="">{{ t('withdrawal.amount') }} (*)</label>
          <input
            type="number"
            step="any"
            class="form-control numbermoney"
            placeholder="0"
            name="amount"
            v-model="amount"
          />
        </div>
        <div class="mb-3" style="flex: auto;">
          <label for="">{{ t('withdrawal.usdtAmount') }} (*)</label>
          <input
            type="number"
            step="any"
            class="form-control numbermoney"
            placeholder="0"
            name="usdtAmount"
            v-model="usdtAmount"
            @focus="isUsdtInputFocused = true"
            @blur="isUsdtInputFocused = false"
          />
          <p class="text-sm text-right font-bold text-gray-500">1 USDT: {{ exchangeRate }}</p>
        </div>
      </div>
      <div class="flex items-center justify-end gap-3 mt-4 mb-2 amount-btns">
        <button
          v-for="btn in amountButtons"
          :key="btn.id"
          type="button"
          class="btn btn-secondary"
          :id="btn.id"
          @click="selectAmount"
          :data-amount="btn.amount"
        >
          {{ btn.label }}
        </button>
        <button type="button" class="btn btn-default" @click="resetAmount">
          {{ t('common.reset') }}
        </button>
      </div>
      <div class="flex items-center gap-3 justify-center mt-4 mb-2">
        <button type="button" class="btn btn-default" @click="handleMyWallet" style="background-color: gray;">
          {{ t('deposit.myWallet') }}
        </button>
        <button type="submit" class="btn btn-default px-4">
          {{ t('common.submit') }}
        </button>
      </div>
    </div>

    <div class="w-full text-center">
      <span class="w-full">{{ t('deposit.bottomNotice') }}</span>
    </div>
  </form>
</template>

<style scoped>
.modal-banner {
  margin-bottom: 1.5rem;
}

.banner-text {
  margin-bottom: 1rem;
}

/* 탭 네비게이션 스타일 */
.tab-navigation {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.tab-btn {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  position: relative;
}

.tab-btn:hover {
  color: #374151;
  background: #f9fafb;
}

.tab-btn.active {
  color: #dc2626;
  border-bottom-color: #dc2626;
  font-weight: 600;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #dc2626;
}

/* 은행 선택 dropdown 스타일 */
.bank-select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
  cursor: pointer;
}

.bank-select:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.amount-btns {
  flex-wrap: wrap;
}

.amount-btns .btn {
  min-width: 100px;
}

@media (max-width: 768px) {
  .amount-btns .btn {
    min-width: 80px;
    font-size: 0.875rem;
  }

  .tab-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
